import { PackageProviderName } from 'data/ppoints/constants';
import { AggregatorPackageProvider } from 'models/ppoints/CountryWithAggregatedProviders';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useShippingProviderCountries } from '../../hooks/useShippingProviderCountries';
import { ShippingProviderCardSimplified } from './ShippingProviderCardSimplified';
import { ShippingProviderCardSimplifiedSkeleton } from './ShippingProviderCardSimplifiedSkeleton';
import { RequestProviderSection } from './components/RequestProviderSection';
import { ShippingProviderCountrySelect } from './components/ShippingProviderCountrySelect';

export const ShippingProviderCardSimplifiedSelector: React.FC<ShippingProviderCardSimplifiedSelectorProps> = (
    props
) => {
    const {
        countries,
        selectedCountry,
        selectedCountryCode,
        setSelectedCountryCode,
        isResolvingCountry,
        skeletonCount,
        getProviderFeatures,
        shopifyLocals,
    } = useShippingProviderCountries({ useLocal: true });

    const { t } = useTranslation();

    const handleOnSelectProvider = (providerName: PackageProviderName) => {
        const isSelected = props.selectedProviders.includes(providerName);
        const updatedProviders = isSelected
            ? props.selectedProviders.filter((name) => name !== providerName)
            : [...props.selectedProviders, providerName];

        props.onProviderSelectionChange(updatedProviders);
    };

    // Replaces this aggregator's own selected carriers (e.g. Kvikk's gls/dpd) with whatever the
    // modal was just saved with, leaving every other provider's selection untouched.
    const handleOnSelectAggregatorProviders = (
        aggregator: AggregatorPackageProvider,
        providerNames: PackageProviderName[]
    ) => {
        const aggregatorProviderNames = aggregator.providers.map((provider) => provider.name);
        const withoutAggregatorProviders = props.selectedProviders.filter(
            (name) => !aggregatorProviderNames.includes(name)
        );

        props.onProviderSelectionChange([...withoutAggregatorProviders, ...providerNames]);
    };

    useEffect(() => {
        if (!selectedCountryCode) return;
        props.onCountryCodeChange?.(selectedCountryCode);
    }, [selectedCountryCode]);

    const hasProviders = (selectedCountry?.packageproviders?.length ?? 0) > 0;

    return (
        <div className="flex flex-col gap-3">
            <div className="max-w-100">
                <ShippingProviderCountrySelect
                    value={selectedCountryCode ?? ''}
                    onChange={setSelectedCountryCode}
                    disabled={isResolvingCountry}
                    countries={countries}
                    shopifyLocals={shopifyLocals}
                />
            </div>
            {!isResolvingCountry && !hasProviders ? (
                // A country the API lists without any provider: there is nothing to select, so the
                // request block takes the place of the provider grid and its label.
                <RequestProviderSection selectedCountryCode={selectedCountryCode} />
            ) : (
                <div>
                    <s-text>{t('SHIPPING_PROVIDERS.SELECT_PROVIDERS_LABEL')}</s-text>
                    <div className="pt-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                        {isResolvingCountry
                            ? Array.from({ length: skeletonCount }, (_, index) => (
                                  <ShippingProviderCardSimplifiedSkeleton key={index} />
                              ))
                            : selectedCountry.packageproviders.map((provider) => (
                                  <ShippingProviderCardSimplified
                                      key={provider.id}
                                      provider={provider}
                                      features={getProviderFeatures(provider.id)}
                                      selectedProviders={props.selectedProviders}
                                      countryCode={selectedCountryCode}
                                      onSelect={handleOnSelectProvider}
                                      onSelectAggregatorProviders={handleOnSelectAggregatorProviders}
                                  />
                              ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export interface ShippingProviderCardSimplifiedSelectorProps {
    selectedProviders: PackageProviderName[];
    onProviderSelectionChange: (providers: PackageProviderName[]) => void;
    /** Fires on the first resolved country as well, not only on a user switch. */
    onCountryCodeChange?: (countryCode: string) => void;
}
